import React, { useEffect, useMemo, useState } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Lock, Scale } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A' };

const MODE_LABEL = {
  refer: { label: 'Back to broker to refer', color: C.amber, desc: 'REFER — returned to the broker, who may refer it to an underwriter by hand' },
  conditions: { label: 'Price with conditions', color: C.green, desc: 'Binds as CONDITIONS with the rule named on the file' },
  ask_broker: { label: 'Ask the broker', color: C.blue, desc: 'The quote waits for the broker\'s answer in the quote screen, then re-prices' },
  decline: { label: 'Decline', color: C.red, desc: 'DECLINE' },
};
const GROUPS = [
  ['information', 'Missing or mismatched information'],
  ['size', 'Size caps'],
  ['appetite', 'Appetite'],
  ['compliance', 'Compliance & integrity — fixed, underwriter queue'],
];

function Num({ label, value, onChange, step = 1, min, max, suffix, testid, disabled }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>{label}</span>
      <div className="flex items-center gap-1.5 mt-1">
        <input type="number" value={value ?? ''} step={step} min={min} max={max} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid={testid}
          className="w-28 px-2 py-1.5 text-sm font-mono rounded-sm bg-white outline-none focus:border-[#1A4C7C] disabled:opacity-60"
          style={{ border: '1px solid rgba(136,150,168,0.35)', color: C.navy }} />
        {suffix && <span className="text-xs" style={{ color: C.slate }}>{suffix}</span>}
      </div>
    </label>
  );
}

export default function ReferralRulesPage() {
  const api = useApi();
  const { user } = useAuth();
  const canEdit = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/referral-rules').then((r) => { setData(r.data); setDraft(r.data.config); }).catch((e) => toast.error(e?.response?.data?.detail || 'Could not load the register'));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => data && draft && JSON.stringify(data.config) !== JSON.stringify(draft), [data, draft]);
  const setMode = (id, mode) => setDraft((d) => ({ ...d, modes: { ...d.modes, [id]: mode } }));
  const setClaims = (k, v) => setDraft((d) => ({ ...d, claims: { ...d.claims, [k]: v === '' ? '' : Number(v) } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: r } = await api.put('/referral-rules', {
        reason, modes: draft.modes, claims: draft.claims, size_cap_factor: Number(draft.size_cap_factor),
        band: { conditions_share_of_refer: Number(draft.band.conditions_share_of_refer) }, info_wait_days: Number(draft.info_wait_days),
      });
      setData(r); setDraft(r.config); setReason('');
      toast.success(`Register saved — version ${r.config.version}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    }
    setSaving(false);
  };

  if (!data || !draft) return <div className="space-y-3 p-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-40 w-full" /></div>;
  const rules = data.rules || [];
  const c = draft.claims;

  return (
    <div className="space-y-4" data-testid="referral-rules-page">
      <Card className="p-5 rounded-sm bg-white border-l-4" style={{ borderLeftColor: C.blue }}>
        <div className="flex items-start gap-3">
          <Scale className="w-5 h-5 mt-0.5" style={{ color: C.blue }} />
          <div>
            <h2 className="text-base font-semibold" style={{ color: C.navy }}>Referral Rules — what each rule does instead of REFER</h2>
            <p className="text-xs mt-1" style={{ color: C.slate }}>
              Owner's ruling {data.ruling}. A broker quote ends one of three ways: bound, back to the broker (answers to give, or their call to refer it), or declined.
              Nothing lands in the underwriter queue automatically except the compliance rules below. Every change here is audited with a reason; version {draft.version}
              {draft.updated_at ? ` · last changed ${new Date(draft.updated_at).toLocaleString('en-AU')} by ${draft.updated_by}` : ''}.
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {Object.entries(MODE_LABEL).map(([k, m]) => (
                <span key={k} className="px-2 py-0.5 rounded-full text-[10px] font-semibold border" style={{ color: m.color, borderColor: `${m.color}40`, background: `${m.color}10` }} title={m.desc}>{m.label}</span>
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 rounded-sm bg-white" data-testid="referral-rules-claims">
          <p className="text-sm font-semibold mb-1" style={{ color: C.navy }}>Claims</p>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>Up to the first number prices with the rate table's loading as CONDITIONS; from the second number, or over the incurred amount, declines (the broker may refer by hand). Between the two: back to the broker.</p>
          <div className="space-y-2">
            <Num label="Auto-price up to" value={c.auto_price_up_to} onChange={(v) => setClaims('auto_price_up_to', v)} min={0} max={9} suffix="claims" testid="claims-auto-price-input" disabled={!canEdit} />
            <Num label="Decline from" value={c.decline_from} onChange={(v) => setClaims('decline_from', v)} min={1} max={10} suffix="claims" testid="claims-decline-from-input" disabled={!canEdit} />
            <Num label="Decline over" value={c.decline_incurred_over} onChange={(v) => setClaims('decline_incurred_over', v)} step={1000} min={0} suffix="$ incurred" testid="claims-decline-incurred-input" disabled={!canEdit} />
          </div>
        </Card>
        <Card className="p-4 rounded-sm bg-white" data-testid="referral-rules-caps">
          <p className="text-sm font-semibold mb-1" style={{ color: C.navy }}>Size caps & score band</p>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>Every class size cap (turnover, sums insured, limits, fleet, headcount) is multiplied by the factor. The band share is how far into the REFER band the CONDITIONS band reaches (0.5 = the upper half).</p>
          <div className="space-y-2">
            <Num label="Size cap factor" value={draft.size_cap_factor} onChange={(v) => setDraft((d) => ({ ...d, size_cap_factor: v }))} step={0.1} min={1} max={3} suffix="× today's caps" testid="size-cap-factor-input" disabled={!canEdit} />
            <Num label="CONDITIONS share of REFER band" value={draft.band.conditions_share_of_refer} onChange={(v) => setDraft((d) => ({ ...d, band: { conditions_share_of_refer: v } }))} step={0.1} min={0} max={0.9} testid="band-share-input" disabled={!canEdit} />
          </div>
        </Card>
        <Card className="p-4 rounded-sm bg-white" data-testid="referral-rules-wait">
          <p className="text-sm font-semibold mb-1" style={{ color: C.navy }}>Waiting on the broker</p>
          <p className="text-[11px] mb-3" style={{ color: C.slate }}>A quote back with the broker — for answers or for their call to refer — expires with the quote.</p>
          <Num label="Wait" value={draft.info_wait_days} onChange={(v) => setDraft((d) => ({ ...d, info_wait_days: v }))} min={1} max={90} suffix="days" testid="info-wait-input" disabled={!canEdit} />
        </Card>
      </div>

      <Card className="rounded-sm bg-white overflow-hidden" data-testid="referral-rules-table">
        {GROUPS.map(([gid, gname]) => {
          const rows = rules.filter((r) => r.group === gid);
          if (!rows.length) return null;
          return (
            <div key={gid}>
              <div className="px-4 py-2 text-[10px] uppercase tracking-[0.14em] border-b" style={{ color: C.slate, background: '#F5F7FA', borderColor: 'rgba(136,150,168,0.15)', fontFamily: 'Georgia, serif' }}>{gname}</div>
              {rows.map((r) => {
                const mode = draft.modes[r.id] || r.default;
                return (
                  <div key={r.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }} data-testid={`referral-rule-${r.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium" style={{ color: C.navy }}>{r.label} <span className="font-mono text-[10px] px-1 rounded ml-1" style={{ background: 'rgba(136,150,168,0.12)', color: C.slate }}>{r.id}</span></p>
                      {r.ask && <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>Asks: "{r.ask.label}"</p>}
                      {r.queue === 'underwriter' && <p className="text-[11px] mt-0.5 inline-flex items-center gap-1" style={{ color: C.slate }}><Lock className="w-3 h-3" />Underwriter queue — a broker cannot resolve this</p>}
                    </div>
                    {r.locked ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold border shrink-0" style={{ color: C.amber, borderColor: `${C.amber}40`, background: `${C.amber}10` }}>REFER (fixed)</span>
                    ) : (
                      <select value={mode} disabled={!canEdit} onChange={(e) => setMode(r.id, e.target.value)} data-testid={`referral-rule-mode-${r.id}`}
                        className="text-xs px-2 py-1.5 rounded-sm bg-white outline-none shrink-0 disabled:opacity-70" style={{ border: `1px solid ${MODE_LABEL[mode]?.color || C.slate}60`, color: MODE_LABEL[mode]?.color || C.navy }}>
                        {data.modes.filter((m) => m !== 'ask_broker' || r.ask || r.id === 'INSUFFICIENT_DATA').map((m) => <option key={m} value={m}>{MODE_LABEL[m].label}</option>)}
                      </select>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </Card>

      {canEdit && (
        <Card className="p-4 rounded-sm bg-white flex flex-col sm:flex-row sm:items-end gap-3" data-testid="referral-rules-save">
          <label className="flex-1 block">
            <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Reason for the change (goes on the audit trail)</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Owner ruling 25 Oct — two claims may price automatically" data-testid="referral-rules-reason"
              className="w-full mt-1 px-3 py-2 text-sm rounded-sm bg-white outline-none focus:border-[#1A4C7C]" style={{ border: '1px solid rgba(136,150,168,0.35)', color: C.navy }} />
          </label>
          <Button disabled={!dirty || reason.trim().length < 10 || saving} onClick={save} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="referral-rules-save-btn">
            {saving ? 'Saving…' : 'Save register'}
          </Button>
          {dirty && <Button variant="ghost" onClick={() => setDraft(data.config)} className="rounded-sm" data-testid="referral-rules-reset-btn">Discard</Button>}
        </Card>
      )}
    </div>
  );
}
